import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useMemo,
} from 'react';
import { MODULE_NAME } from '../constants/moduleName';
import { THEME_BLUE } from '../constants/themeConstants';

const ConfigContext = createContext(null);

export const ConfigContextProvider = ({
  children,
  defaultChildAge,
  defaultValue,
  element,
  errors,
  hasAdultAgesHint,
  hasChildrenAges,
  hasInfants,
  hasInfantsAges,
  hasLabel,
  hasPets,
  hasRooms,
  inputLabel,
  lob,
  maxAdults,
  maxChildAge,
  maxChildren,
  maxCombinedChildrenAndInfants,
  maxInfants,
  maxRooms,
  maxTravelersCap,
  maxTravelersPerRoom,
  onChange,
  onToggleVisibility,
  parentId,
  shouldAppendDropdownLabel,
  theme,
  value,
}) => {
  const memoized = useMemo(() => ({
    defaultChildAge,
    defaultValue,
    element,
    errors,
    hasAdultAgesHint,
    hasChildrenAges,
    hasInfants,
    hasInfantsAges,
    hasLabel,
    hasPets,
    hasRooms,
    inputLabel,
    lob,
    maxAdults,
    maxChildAge,
    maxChildren,
    maxCombinedChildrenAndInfants,
    maxInfants,
    maxRooms,
    maxTravelersCap,
    maxTravelersPerRoom,
    moduleId: parentId ? `${MODULE_NAME}-${parentId}` : MODULE_NAME,
    onChange,
    onToggleVisibility,
    shouldAppendDropdownLabel,
    theme,
    value,
  }), [
    defaultChildAge,
    defaultValue,
    element,
    errors,
    hasAdultAgesHint,
    hasChildrenAges,
    hasInfants,
    hasInfantsAges,
    hasLabel,
    hasPets,
    hasRooms,
    inputLabel,
    lob,
    maxAdults,
    maxChildAge,
    maxChildren,
    maxCombinedChildrenAndInfants,
    maxInfants,
    maxRooms,
    maxTravelersCap,
    maxTravelersPerRoom,
    onChange,
    onToggleVisibility,
    parentId,
    shouldAppendDropdownLabel,
    theme,
    value,
  ]);

  return (
    <ConfigContext.Provider value={memoized}>
      {children}
    </ConfigContext.Provider>
  );
};

ConfigContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultChildAge: PropTypes.string,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({})
  ),
  element: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  hasAdultAgesHint: PropTypes.bool,
  hasChildrenAges: PropTypes.bool,
  hasInfants: PropTypes.bool,
  hasInfantsAges: PropTypes.func,
  hasLabel: PropTypes.bool,
  hasPets: PropTypes.bool,
  hasRooms: PropTypes.bool,
  inputLabel: PropTypes.string,
  lob: PropTypes.string,
  maxAdults: PropTypes.number,
  maxChildAge: PropTypes.number,
  maxChildren: PropTypes.number,
  maxCombinedChildrenAndInfants: PropTypes.number,
  maxInfants: PropTypes.number,
  maxRooms: PropTypes.number,
  maxTravelersCap: PropTypes.number,
  maxTravelersPerRoom: PropTypes.number,
  onChange: PropTypes.func,
  onToggleVisibility: PropTypes.func,
  parentId: PropTypes.string,
  shouldAppendDropdownLabel: PropTypes.bool,
  theme: PropTypes.string,
  value: PropTypes.arrayOf(
    PropTypes.shape({})
  ).isRequired,
};

ConfigContextProvider.defaultProps = {
  theme: THEME_BLUE,
};

export const useConfigContext = () => {
  const context = useContext(ConfigContext);

  if (!context || Object.keys(context).length === 0) {
    throw new Error('useConfigContext must be used within ConfigContextProvider.');
  }

  return context;
};
